import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import type { TransactionRow } from '@/services/transactions';

interface TransactionDetailModalProps {
  transaction: TransactionRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (updates: any) => void;
}

export const TransactionDetailModal = ({ transaction, open, onOpenChange, onSave }: TransactionDetailModalProps) => {
  const { toast } = useToast();
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    account_number: '',
    counterparty: '',
    description: '',
    amount: '',
    vat_rate: '',
    notes: '',
  });
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  if (!transaction) return null;

  const handleEdit = () => {
    setFormData({
      account_number: transaction.account_number || '',
      counterparty: transaction.counterparty || '',
      description: transaction.description || '',
      amount: transaction.amount.toString(),
      vat_rate: transaction.swiss_vat_rate?.toString() || '0',
      notes: '',
    });
    setEditMode(true);
  };

  const handleSave = () => {
    onSave?.({
      id: transaction.id,
      account_number: formData.account_number,
      counterparty: formData.counterparty,
      description: formData.description,
      amount: parseFloat(formData.amount),
      human_verified: true,
      needs_review: false,
    });
    toast({
      title: "Transaktion aktualisiert",
      description: "Die Änderungen wurden gespeichert.",
    });
    setEditMode(false);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setUploadedFiles(prev => [...prev, ...files]);
      toast({
        title: "Beleg hochgeladen",
        description: `${files.length} Datei(en) hinzugefügt`,
      });
    }
  };

  const confidenceScore = transaction.ai_confidence_score ?? 0;
  const confidenceColor = confidenceScore >= 90 ? 'text-green-600' : confidenceScore >= 70 ? 'text-yellow-600' : 'text-red-600';
  const confidenceBg = confidenceScore >= 90 ? 'bg-green-50' : confidenceScore >= 70 ? 'bg-yellow-50' : 'bg-red-50';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Transaktionsdetails</span>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${confidenceBg} ${confidenceColor}`}>
              {confidenceScore.toFixed(1)}% Confidence
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-brand-text-secondary">Datum</Label>
              <p className="text-sm font-medium text-brand-text-primary mt-1">
                {new Date(transaction.transaction_date).toLocaleDateString('de-CH')}
              </p>
            </div>
            <div>
              <Label className="text-xs text-brand-text-secondary">Betrag</Label>
              <p className={`text-sm font-bold mt-1 ${transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                CHF {transaction.amount.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Editable Fields */}
          {editMode ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="counterparty">Gegenpartei</Label>
                <Input
                  id="counterparty"
                  value={formData.counterparty}
                  onChange={(e) => setFormData(prev => ({ ...prev, counterparty: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Beschreibung</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="account">Konto</Label>
                  <Input
                    id="account"
                    value={formData.account_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, account_number: e.target.value }))}
                    placeholder="z.B. 3200"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vat">MWST (%)</Label>
                  <Select
                    value={formData.vat_rate}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, vat_rate: value }))}
                  >
                    <SelectTrigger id="vat">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0% (Keine MWST)</SelectItem>
                      <SelectItem value="2.6">2.6% (reduziert)</SelectItem>
                      <SelectItem value="3.8">3.8% (Sondersatz)</SelectItem>
                      <SelectItem value="8.1">8.1% (Standard)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notizen</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Interne Notizen..."
                  rows={2}
                />
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label className="text-xs text-brand-text-secondary">Gegenpartei</Label>
                <p className="text-sm text-brand-text-primary">{transaction.counterparty || 'Keine Angabe'}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-brand-text-secondary">Beschreibung</Label>
                <p className="text-sm text-brand-text-primary">{transaction.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-brand-text-secondary">Konto</Label>
                  <p className="text-sm font-medium text-brand-text-primary">
                    {transaction.account_number || 'Nicht klassifiziert'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-brand-text-secondary">MWST</Label>
                  <p className="text-sm font-medium text-brand-text-primary">
                    {transaction.swiss_vat_rate ? `${transaction.swiss_vat_rate}%` : 'Keine'}
                  </p>
                </div>
              </div>
              {transaction.ai_reasoning && (
                <div className="space-y-2">
                  <Label className="text-xs text-brand-text-secondary">KI-Begründung</Label>
                  <p className="text-sm text-brand-text-primary bg-brand-secondary/30 p-3 rounded-lg">
                    {transaction.ai_reasoning}
                  </p>
                </div>
              )}
            </>
          )}

          {/* File Upload Section */}
          <div className="space-y-3 border-t pt-4">
            <Label className="text-sm font-medium">Belege & Quittungen</Label>
            <div className="flex items-center gap-3">
              <Input
                type="file"
                onChange={handleFileUpload}
                multiple
                accept="image/*,.pdf"
                className="flex-1"
              />
              <Button variant="outline" size="sm" onClick={() => document.querySelector<HTMLInputElement>('input[type="file"]')?.click()}>
                <i className="fa-solid fa-paperclip mr-2"></i>
                Hochladen
              </Button>
            </div>
            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                {uploadedFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-brand-secondary/30 p-2 rounded-lg">
                    <div className="flex items-center gap-2">
                      <i className="fa-solid fa-file-pdf text-brand-accent"></i>
                      <span className="text-sm text-brand-text-primary">{file.name}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setUploadedFiles(prev => prev.filter((_, i) => i !== idx))}
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          {editMode ? (
            <>
              <Button variant="outline" onClick={() => setEditMode(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSave} className="bg-brand-primary text-white">
                <i className="fa-solid fa-check mr-2"></i>
                Speichern
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Schließen
              </Button>
              <Button onClick={handleEdit} className="bg-brand-primary text-white">
                <i className="fa-solid fa-pen mr-2"></i>
                Edit
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
